import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { Breadcrumbs, H5 } from '../../../../AbstractElements';
import { Card, CardBody, CardHeader, Col, Container, Row, Input, Button } from 'reactstrap';
import DataTable from 'react-data-table-component';
import { PlusCircle, Trash2, Download } from 'react-feather';
import { toast } from 'react-toastify';
import DeleteConfirmationModal from '../../../Common/Modals/DeleteConfirmationModal';
import ReportTypeDataModal from './ReportTypeDataModal';
import { confirmDelete, fetchTableData } from '../../../../Api/ApiMethods';

const ReportTypes = () => {
    const [selectedRows, setSelectedRows] = useState([]);
    const [clearSelectedRows, setClearSelectedRows] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState('');
    const [totalRows, setTotalRows] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [selectedReportType, setSelectedReportType] = useState(null);

    const reportTypeApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/filetemplate';

    const fetchData = useCallback(() => {
        fetchTableData({
            apiEndpoint: reportTypeApiUrl,
            page,
            pageSize,
            search,
            setDataCallback: setData,
            setTotalRowsCallback: setTotalRows,
        });
    }, [reportTypeApiUrl, page, pageSize, search]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const toggleDeleteModal = () => setDeleteModal(!deleteModal);

    const toggleModal = () => {
        setModalOpen(!modalOpen);
        if (modalOpen) {
            setSelectedReportType(null);
            setModalMode('add');
        }
    };

    const openAddModal = () => {
        setModalMode('add');
        setSelectedReportType(null);
        toggleModal();
    };

    const handleRowSelected = useCallback(state => {
        setSelectedRows(state.selectedRows);
    }, []);

    const handleSearch = (event) => {
        setSearch(event.target.value);
        setPage(1);
    };

    const columns = [
        { name: 'ID', selector: row => row.id, sortable: true, omit: true },
        { name: 'Başlık', selector: row => row.title, sortable: true },
        { 
            name: 'Rapor Türü', 
            selector: row => row.isExpertReport ? 'Eksper Raporu' : 'Sigorta Dilekçesi', 
            sortable: true 
        },
        {
            name: 'İndir',
            cell: (row) => (
                <Button
                    color="info"
                    size="sm"
                    className="d-flex align-items-center justify-content-center"
                    onClick={() => {
                        if (row.fileId) {
                            const downloadUrl = `${process.env.REACT_APP_FILE_API_PREVIEW_ENDPOINT}/${row.fileId}`;
                            window.open(downloadUrl, '_blank');
                        } else {
                            toast.error("İndirme linki için dosya ID bulunamadı.");
                            console.error("Error: fileId is missing for row:", row);
                        }
                    }}
                    title="İndir"
                    disabled={!row.fileId}
                >
                    <Download size={18} />
                </Button>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            right: true,
            width: '100px'
        },
    ];

    return (
        <Fragment>
            <Breadcrumbs parent='Tanımlamalar' title='Rapor Şablonları' />
            <Container fluid={true}>
                <Row>
                    <Col sm='12'>
                        <Card>
                            <CardHeader>
                                <H5>Rapor Şablonları Listesi</H5>
                                <span>Yeni rapor şablonları oluşturabilir ve mevcut şablonları yönetebilirsiniz.</span>
                            </CardHeader>
                            <CardBody>
                                <div className="d-flex justify-content-end align-items-center w-100 file-content flex-wrap">
                                    <div className='form-inline me-2 mt-3 p-0 custom-responsive-width'>
                                        <div className='mb-0 form-group d-flex align-items-center border rounded px-2' style={{ height: '35px' }}>
                                            <i className='fa fa-search text-muted'></i>
                                            <Input
                                                type="text"
                                                placeholder="Ara.."
                                                value={search}
                                                onChange={handleSearch}
                                                className="border-0 ms-2 shadow-none"
                                                style={{ height: '33px', flexGrow: 1 }}
                                            />
                                        </div>
                                    </div>
                                    <div className='btn btn-outline-primary ms-2 mt-3' onClick={openAddModal} style={{ height: '35px', display: 'inline-flex', alignItems: 'center' }}>
                                        <PlusCircle size={18} className="me-1"/>
                                        {' Şablon Ekle'}
                                    </div>
                                    {(selectedRows.length > 0) &&
                                        <div className='btn btn-outline-danger ms-2 mt-3' onClick={toggleDeleteModal} style={{ height: '35px', display: 'inline-flex', alignItems: 'center' }}>
                                            <Trash2 size={18} className="me-1"/>
                                            {'Seçilenleri Sil'}
                                        </div>
                                    }
                                </div>
                                <div style={{ height: "40px" }}></div>
                                <Fragment>
                                    <DataTable
                                        columns={columns}
                                        data={data}
                                        onSelectedRowsChange={handleRowSelected}
                                        pagination
                                        paginationServer
                                        selectableRows
                                        clearSelectedRows={clearSelectedRows}
                                        paginationTotalRows={totalRows}
                                        onChangePage={page => setPage(page)}
                                        onChangeRowsPerPage={newPerPage => {
                                            setPageSize(newPerPage);
                                            setPage(1);
                                        }}
                                        noDataComponent="Gösterilecek rapor şablonu bulunamadı."
                                    />
                                </Fragment>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
            <ReportTypeDataModal
                apiEndpoint={reportTypeApiUrl}
                isOpen={modalOpen}
                toggle={toggleModal}
                mode={modalMode}
                selectedReportType={selectedReportType}
                refreshData={fetchData}
            />
            <DeleteConfirmationModal
                isOpen={deleteModal}
                toggle={toggleDeleteModal}
                itemsToDelete={selectedRows}
                displayKey="title"
                onConfirm={() =>
                    confirmDelete({
                        apiEndpoint: reportTypeApiUrl,
                        selectedItems: selectedRows,
                        toggleModalCallback: toggleDeleteModal,
                        clearSelectionCallback: () => {
                            setSelectedRows([]);
                            setClearSelectedRows(!clearSelectedRows);
                        },
                        refreshDataCallback: fetchData,
                        componentName: "Rapor Şablonu"
                    })
                }
            />
        </Fragment>
    );
};

export default ReportTypes;
